import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import Scrollbars from "react-custom-scrollbars-2";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, X } from "react-feather";
import toast from "react-hot-toast";
import { navSections } from "./navConfig";
import { useClaims } from "../../features/auth/useClaims";
import { useListQuickMenuItemsQuery, useUnpinPageMutation } from "../../features/quickMenu/quickMenuApi";
import { iconComponentFor } from "../../features/quickMenu/resolvePageMeta";

// NavLink's own isActive is a plain "pathname starts with this path" prefix
// match, so a nav item whose path is itself a prefix of a sibling item's path
// (e.g. "/masters/customers" vs "/masters/customers/locations-map", or
// "/stock/lpos" vs "/stock/lpos/pending-vehicle-requests") lit up BOTH items
// at once when the more specific one's page was open. Only the longest
// (most specific) matching path across the whole nav should ever be active -
// computed once here and used instead of each NavLink's own isActive.
const findActivePath = (pathname, sections) => {
  const candidates = sections
    .flatMap((s) => s.items.filter((i) => i.path).map((i) => i.path))
    .filter((p) => pathname === p || pathname.startsWith(`${p}/`));
  candidates.sort((a, b) => b.length - a.length);
  return candidates[0];
};

// A section's items can include non-clickable `{ isGroupLabel: true, label }`
// rows (Reports' "Stock"/"Petty Cash" sub-headers) - always kept through the
// permission filter itself (they carry no permission), but a group label
// immediately followed by nothing (every item under it was permission-
// filtered out, or it's the section's trailing item) would render as an
// empty heading, so it's dropped in this second pass instead.
// item.permission can also be an array - shown if the caller holds ANY one
// of them (e.g. Guard Lookup, gated the same "Attendance.Mark OR
// Attendance.View" way the backend policy is).
const itemIsVisible = (item, permissions) =>
  !item.permission ||
  (Array.isArray(item.permission) ? item.permission.some((p) => permissions.includes(p)) : permissions.includes(item.permission));

const filterSectionItems = (items, permissions) => {
  const visible = items.filter((item) => itemIsVisible(item, permissions));
  return visible.filter((item, idx) => !item.isGroupLabel || (visible[idx + 1] && !visible[idx + 1].isGroupLabel));
};

// v2 key (2026-09-23): the menu now starts COLLAPSED - every section closed except Main
// (Dashboard / One Stop / Quick Menu) and the section holding the page that is open.
// A new key so everyone starts from that default once; their own toggles persist from then on.
const COLLAPSED_STORAGE_KEY = "texasalarms-sidebar-collapsed-sections-v2";
const ALWAYS_OPEN = ["Main"];

const loadCollapsed = (sections, pathname) => {
  try {
    const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (stored) return new Set(JSON.parse(stored));
  } catch {
    // fall through to the default
  }
  const holdsCurrentPage = (s) => s.items.some((i) => i.path && (pathname === i.path || pathname.startsWith(`${i.path}/`)));
  return new Set(sections.filter((s) => !ALWAYS_OPEN.includes(s.label) && !holdsCurrentPage(s)).map((s) => s.label));
};

// Section headers (HR Masters, Stock Management, Administration, ...) are
// now click-to-collapse - the module list had grown enough that finding the
// last menu meant scrolling past every section above it. Collapsed state is
// per-section and persisted to localStorage (same pattern as the Feedback
// widget's reporter-name persistence) so it survives a page reload instead
// of resetting to fully-expanded every time.
const Sidebar = () => {
  const { permissions } = useClaims();
  const location = useLocation();
  const { data: quickMenuItems = [] } = useListQuickMenuItemsQuery();
  const [unpinPage] = useUnpinPageMutation();

  // Quick Menu items are per-user/dynamic, so they can't live in the static
  // navConfig.js export - merged onto the "Main" section here at render
  // time instead, each flagged isQuickMenuItem so the row below can offer
  // a remove "x" (the toggle button in QuickPinButton.jsx is the other
  // removal path for the same underlying pin).
  const sections = navSections.map((section) =>
    section.label === "Main"
      ? {
          ...section,
          items: [
            ...section.items,
            ...quickMenuItems.map((qi) => ({
              path: qi.pagePath,
              label: qi.pageLabel,
              icon: iconComponentFor(qi.pageIcon),
              isQuickMenuItem: true,
              quickMenuItemId: qi.quickMenuItemId,
            })),
          ],
        }
      : section
  );
  const activePath = findActivePath(location.pathname, sections);
  const [collapsed, setCollapsed] = useState(() => loadCollapsed(navSections, location.pathname));

  const handleUnpin = async (e, quickMenuItemId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await unpinPage(quickMenuItemId).unwrap();
    } catch (error) {
      toast.error(error?.message || "Could not remove from Quick Menu");
    }
  };
  // Mini-sidebar (icon rail) hover flyout - portaled straight to <body>
  // (2026-08-29) rather than positioned inline, because react-custom-
  // scrollbars-2 wraps this menu in an overflow:hidden view div to hide its
  // own native scrollbar; an absolutely-positioned flyout nested inside that
  // view was being silently clipped no matter its z-index. { section, top }
  // or null.
  const [flyout, setFlyout] = useState(null);
  const closeTimer = useRef(null);

  const toggleSection = (label) => {
    setFlyout(null); // opening a section inline replaces its flyout
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setFlyout(null), 150);
  };
  // Icon rail: every section flies out on hover (its items are hidden in the rail).
  // Full sidebar (2026-09-23, "show the sub menus on mouse over as it is in the icon bar"):
  // a COLLAPSED section flies out too, to the right of the sidebar; an open section
  // already shows its items inline, so no flyout there.
  const openFlyout = (section, items, e, isCollapsed) => {
    const mini = document.body.classList.contains("mini-sidebar");
    if (!mini && !isCollapsed) return;
    cancelClose();
    const rect = e.currentTarget.getBoundingClientRect();
    setFlyout({ section, items, top: rect.top, left: mini ? undefined : rect.right });
  };

  return (
    <div className="sidebar" id="sidebar">
      <Scrollbars>
        <div className="sidebar-inner slimscroll">
          <div id="sidebar-menu" className="sidebar-menu">
            <ul>
              {sections.map((section) => {
                const items = filterSectionItems(section.items, permissions);
                if (items.length === 0 || items.every((item) => item.isGroupLabel)) return null;
                const isCollapsed = collapsed.has(section.label);
                const SectionIcon = section.icon;

                // The child <ul> is always rendered now (2026-08-29 fix) -
                // visibility for the accordion is a CSS class
                // (.section-collapsed), not JSX omission. That's what lets
                // mini-sidebar mode find a section's icon in the DOM even
                // when that section's accordion was left collapsed -
                // previously the icons for a collapsed section simply
                // didn't exist to show at all in icon-rail mode.
                return (
                  <li
                    className={`submenu-open sidebar-section${isCollapsed ? " section-collapsed" : ""}`}
                    key={section.label}
                    onMouseEnter={(e) => openFlyout(section, items, e, isCollapsed)}
                    onMouseLeave={scheduleClose}
                  >
                    <h6
                      className="submenu-hdr d-flex align-items-center justify-content-between"
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleSection(section.label)}
                      role="button"
                      tabIndex={0}
                      title={section.label}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleSection(section.label);
                        }
                      }}
                    >
                      <span className="d-flex align-items-center">
                        {SectionIcon && <SectionIcon size={16} className="sidebar-section-icon me-2" />}
                        <span className="sidebar-section-label">{section.label}</span>
                      </span>
                      {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                    </h6>
                    <ul className="sidebar-section-items">
                      {items.map((item) => {
                        if (item.isGroupLabel) {
                          return (
                            <li
                              key={`group-${item.label}`}
                              className="sidebar-group-label"
                              style={{ padding: "6px 20px 4px", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", opacity: 0.6 }}
                            >
                              {item.label}
                            </li>
                          );
                        }
                        const Icon = item.icon;
                        return (
                          <li key={item.path} className="d-flex align-items-center">
                            <NavLink
                              to={item.path}
                              title={item.label}
                              className={item.path === activePath ? "active" : ""}
                              style={{ flex: 1 }}
                            >
                              <Icon />
                              <span>{item.label}</span>
                            </NavLink>
                            {item.isQuickMenuItem && (
                              <button
                                type="button"
                                className="btn btn-link p-1 text-muted"
                                title="Remove from Quick Menu"
                                onClick={(e) => handleUnpin(e, item.quickMenuItemId)}
                              >
                                <X size={13} />
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </Scrollbars>

      {flyout &&
        createPortal(
          <ul
            className="sidebar-flyout-portal"
            style={{ top: flyout.top, ...(flyout.left != null ? { left: flyout.left } : {}) }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <li className="sidebar-flyout-title">{flyout.section.label}</li>
            {flyout.items.map((item) => {
              if (item.isGroupLabel) {
                return (
                  <li
                    key={`group-${item.label}`}
                    style={{ padding: "6px 16px 4px", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", opacity: 0.6 }}
                  >
                    {item.label}
                  </li>
                );
              }
              const Icon = item.icon;
              return (
                <li key={item.path} className="d-flex align-items-center">
                  <NavLink
                    to={item.path}
                    className={item.path === activePath ? "active" : ""}
                    onClick={() => setFlyout(null)}
                    style={{ flex: 1 }}
                  >
                    <Icon size={16} />
                    <span>{item.label}</span>
                  </NavLink>
                  {item.isQuickMenuItem && (
                    <button
                      type="button"
                      className="btn btn-link p-1 text-muted"
                      title="Remove from Quick Menu"
                      onClick={(e) => handleUnpin(e, item.quickMenuItemId)}
                    >
                      <X size={13} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default Sidebar;
